/// <reference path="./vite-env.d.ts" />
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Minimal tenant information returned by anonymous endpoints
 * Used for multi-tenant routing and branding on login/registration pages
 */
export interface TenantBasicInfo {
  tenantID: number;
  businessName: string;
  tenantSlug: string;
}

/**
 * API response format from tenant endpoints
 */
interface TenantApiResponse {
  data: TenantBasicInfo[];
  errors: any[];
  success: boolean;
}

/**
 * State interface for auto-detect tenant store
 */
export interface AutoDetectTenantState {
  // State
  tenant: TenantBasicInfo | null;
  loading: boolean;
  error: string | null;
  lastFetchTime: number | null;

  // Actions
  loadTenant: () => Promise<void>;
  loadTenantBySlug: (slug: string) => Promise<void>;
  setTenant: (tenant: TenantBasicInfo) => void;
  clearTenant: () => void;
  clearError: () => void;
}

/**
 * Get API base URL from environment or fallback
 */
function getApiBaseUrl(): string {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return 'http://localhost:10001';
}

/**
 * Helper function to fetch tenant data from the API
 */
async function fetchTenantData(path: string): Promise<TenantBasicInfo | null> {
  const baseUrl = getApiBaseUrl();
  // Strip any trailing slash from baseUrl before appending path (which always starts
  // with '/') -- otherwise a baseUrl of exactly '/' (the recommended relative-root
  // value, see getApiBaseUrl above) produces '//api/...', which browsers parse as a
  // protocol-relative URL: everything up to the next '/' becomes the *hostname*,
  // so the request tries to resolve a host literally named "api" and fails with
  // ERR_NAME_NOT_RESOLVED instead of hitting this origin's /api/... route.
  const url = `${baseUrl.replace(/\/$/, '')}${path}`;

  console.log('[AutoDetectTenantStore] Fetching tenant from:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: Failed to fetch tenant info from ${url}`);
  }

  const data: TenantApiResponse = await response.json();

  if (!data.data || data.data.length === 0) {
    throw new Error('No tenant data in response');
  }

  return data.data[0] ?? null;
}

/**
 * Zustand store for auto-detecting tenant from hostname
 * Persists to localStorage to survive page reloads
 *
 * Usage:
 * ```typescript
 * const { tenant, loading, error } = useAutoDetectTenantStore();
 *
 * useEffect(() => {
 *   useAutoDetectTenantStore.getState().loadTenant();
 * }, []);
 * ```
 */
export const useAutoDetectTenantStore = create<AutoDetectTenantState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        tenant: null,
        loading: false,
        error: null,
        lastFetchTime: null,

        /**
         * Auto-detect tenant from current hostname
         * Calls: GET /api/v1/iam/tenants/info
         */
        loadTenant: async () => {
          // Avoid duplicate requests within 5 seconds
          const now = Date.now();
          const lastFetch = get().lastFetchTime;
          if (lastFetch && now - lastFetch < 5000 && get().tenant) {
            console.log('[AutoDetectTenantStore] Using cached tenant data (within 5s dedup window)');
            return;
          }

          set({ loading: true, error: null });
          try {
            const tenant = await fetchTenantData('/api/v1/iam/tenants/info');
            set({
              tenant,
              loading: false,
              error: null,
              lastFetchTime: now,
            });
            console.log('[AutoDetectTenantStore] Tenant loaded successfully:', tenant?.businessName);
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            set({
              error: errorMessage,
              loading: false,
              lastFetchTime: now,
            });
            console.error('[AutoDetectTenantStore] Error loading tenant:', errorMessage);
          }
        },

        /**
         * Load tenant by explicit slug
         * Calls: GET /api/v1/iam/tenants/by-slug/{slug}
         */
        loadTenantBySlug: async (slug: string) => {
          if (!slug) {
            set({ error: 'Slug is required' });
            return;
          }

          set({ loading: true, error: null });
          try {
            const path = `/api/v1/iam/tenants/by-slug/${encodeURIComponent(slug)}`;
            const tenant = await fetchTenantData(path);
            set({
              tenant,
              loading: false,
              error: null,
              lastFetchTime: Date.now(),
            });
            console.log('[AutoDetectTenantStore] Tenant loaded by slug:', slug, tenant?.businessName);
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            set({
              error: errorMessage,
              loading: false,
              lastFetchTime: Date.now(),
            });
            console.error('[AutoDetectTenantStore] Error loading tenant by slug:', slug, errorMessage);
          }
        },

        /**
         * Manually set tenant (useful for testing or explicit assignment)
         */
        setTenant: (tenant: TenantBasicInfo) => {
          set({
            tenant,
            error: null,
            lastFetchTime: Date.now(),
          });
        },

        /**
         * Clear tenant and cached data
         */
        clearTenant: () => {
          set({
            tenant: null,
            error: null,
            loading: false,
            lastFetchTime: null,
          });
          if (typeof window !== 'undefined') {
            localStorage.removeItem('tenant-auto-detect');
          }
        },

        /**
         * Clear error state without clearing tenant
         */
        clearError: () => {
          set({ error: null });
        },
      }),
      {
        name: 'tenant-auto-detect',
        partialize: (state) => ({
          tenant: state.tenant,
          lastFetchTime: state.lastFetchTime,
        }),
      }
    )
  )
);

/**
 * Hook for using auto-detect tenant in React components
 * Returns tenant data, loading state, and error state
 *
 * Example:
 * ```typescript
 * function LoginPage() {
 *   const { tenant, loading, error } = useAutoDetectTenantStore();
 *
 *   useEffect(() => {
 *     useAutoDetectTenantStore.getState().loadTenant();
 *   }, []);
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <Error message={error} />;
 *
 *   return <div>Welcome to {tenant?.businessName}</div>;
 * }
 * ```
 */
export const useAutoDetectTenant = () => {
  return useAutoDetectTenantStore();
};
